import { ReactElement, ReactNode, useEffect } from 'react';
import { handleSignInUser, useAuthState } from '../auth/AuthProvider';
import { useAccountContext } from '../context/AccountContext';
import { Account, Organization } from '../types/Account';
import { AccountErrorMessage } from './AccountErrorMessage';
import LoaderDots from './LoaderDots';

export type RequireSignInRenderProps = {
  account: Account;
  /** only loaded for org admins */
  organization?: Organization;
};

type RequireSignInProps = {
  children: (props: RequireSignInRenderProps) => ReactNode;
};

/**
 * Calls `children` with the loaded `account` (and `organization`) only once the
 * user is signed in and their `Account` has loaded, showing a loader until then
 * (or an error if the lookup fails). Signed-out users are sent to the hosted
 * sign-in UI, which returns them to this page afterwards.
 *
 * @example
 * <RequireSignIn>
 *   {({ account }) => <BetaFeedbackForm account={account} />}
 * </RequireSignIn>
 */
export const RequireSignIn = ({
  children,
}: RequireSignInProps): ReactElement => {
  const { authState } = useAuthState();
  const { loadingState, account, organization } = useAccountContext();
  const signedOut = authState === 'signIn';

  useEffect(() => {
    if (signedOut) {
      handleSignInUser();
    }
  }, [signedOut]);

  if (authState === 'signedIn' && loadingState === 'complete' && account) {
    return <>{children({ account, organization })}</>;
  }
  if (authState === 'signedIn' && loadingState === 'error') {
    return <AccountErrorMessage />;
  }
  return <LoaderDots className="mx-auto mt-10 block" />;
};
